import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Avatar } from './communs';
import { STATUTS_TACHE, PRIORITES, libelle } from '../constants';
import { aujourdhui, dateCourte, enRetard, nomComplet } from '../utils/format';

/**
 * Fresque (vue planning) : les projets et, sous chacun, une ligne par tâche
 * datée, posées sur une frise de mois.
 *   - ligne de projet → bande de ses dates (début → échéance) ;
 *   - tâche avec début et échéance → barre ;
 *   - tâche avec une seule des deux dates → jalon (épingle) ;
 *   - tâche sans date → comptée sous le nom du projet.
 * Une barre se déplace en la glissant ; ses bords s'étirent pour changer le
 * début ou l'échéance. Un jalon se glisse aussi. Pendant le geste, une bulle
 * affiche la date sous le curseur ; Échap annule.
 * La colonne de gauche et l'en-tête des mois restent visibles au défilement.
 */

const ZOOM_MIN = 2;   // pixels par jour
const ZOOM_MAX = 24;
const CLE_ZOOM = 'a97projets_zoom_fresque';
const SEUIL = 4;      // pixels avant qu'un clic devienne un glissé
const BORD = 40;      // zone de défilement automatique au bord de la frise

// 'AAAA-MM-JJ' → numéro de jour (UTC : aucun décalage de fuseau possible).
const jour = (s) => { const [a, m, j] = s.split('-').map(Number); return Date.UTC(a, m - 1, j) / 864e5; };
const versDate = (n) => new Date(n * 864e5);
const versJour = (n) => versDate(n).toISOString().slice(0, 10);
const dateLongue = (n) => versDate(n).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

function etat(t) {
  if (t.statut === 'termine') return { classe: 'vert', signe: '' };
  if (enRetard(t.date_echeance || t.date_debut, t.statut)) return { classe: 'rouge', signe: '!' };
  if (t.priorite === 'haute') return { classe: 'orange', signe: '' };
  return { classe: 'bleu', signe: '' };
}

// Jours occupés par une tâche datée : [a, b] inclus.
function bornes(t) {
  if (t.date_debut && t.date_echeance) return { a: jour(t.date_debut), b: jour(t.date_echeance), jalon: false };
  const n = jour(t.date_echeance || t.date_debut);
  return { a: n, b: n, jalon: true };
}

const lireZoom = () => {
  try { const z = Number(localStorage.getItem(CLE_ZOOM)); return z >= ZOOM_MIN && z <= ZOOM_MAX ? z : 8; } catch { return 8; }
};

export default function Fresque({ projets, taches, onOuvrirTache, onOuvrirProjet, onChangerDates }) {
  const [zoom, setZoom] = useState(lireZoom);
  const zone = useRef(null);
  const ancre = useRef(null);   // jour au centre de l'écran, conservé quand on zoome
  const debutPrec = useRef(null);
  const geste = useRef(null);   // glissé en cours (valeurs de travail)
  const sansClic = useRef(false); // le clic qui suit un glissé n'ouvre pas la fiche
  const [glisse, setGlisse] = useState(null); // { id, mode, a, b, cx, cy } pour l'affichage
  const auj = jour(aujourdhui());
  const gauche = window.innerWidth < 700 ? 150 : 260; // colonne de gauche
  const [visible, setVisible] = useState(1200); // largeur affichée de la frise

  // La frise remplit toujours au moins la largeur affichée (pas de vide à droite).
  useEffect(() => {
    const mesurer = () => zone.current && setVisible(zone.current.clientWidth - gauche);
    mesurer();
    window.addEventListener('resize', mesurer);
    return () => window.removeEventListener('resize', mesurer);
  }, [gauche]);
  const joursMin = Math.ceil(visible / zoom) + 31;

  // Étendue de la frise : toutes les dates connues avec un mois de marge de
  // chaque côté (de quoi y glisser une barre), et au moins un mois avant et
  // deux mois après aujourd'hui ; calée sur des débuts de mois.
  const { debut, fin, mois } = useMemo(() => {
    const dates = [auj, auj + 60];
    for (const p of projets) for (const d of [p.date_debut, p.date_echeance]) if (d) dates.push(jour(d));
    for (const t of taches) for (const d of [t.date_debut, t.date_echeance]) if (d) dates.push(jour(d));
    const a = versDate(Math.min(...dates) - 30);
    const b = versDate(Math.max(...dates));
    const d0 = Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), 1) / 864e5;
    const d1 = Math.max(
      Date.UTC(b.getUTCFullYear(), b.getUTCMonth() + 2, 1) / 864e5, // un mois de marge
      d0 + joursMin
    );
    const liste = [];
    for (let y = a.getUTCFullYear(), m = a.getUTCMonth(); ; m++) {
      const n = Date.UTC(y, m, 1) / 864e5;
      if (n >= d1) break;
      liste.push({ n, date: versDate(n), jours: Date.UTC(y, m + 1, 1) / 864e5 - n });
    }
    // Fin calée sur la fin du dernier mois affiché.
    const dernier = liste[liste.length - 1];
    return { debut: d0, fin: dernier.n + dernier.jours, mois: liste };
  }, [projets, taches, auj, joursMin]);

  const x = (n) => (n - debut) * zoom;
  const largeur = (fin - debut) * zoom;

  // Groupes : un projet, puis ses tâches datées (dans l'ordre chronologique
  // reçu du serveur), une par ligne.
  const groupes = useMemo(() => projets.map((p) => {
    const siennes = taches.filter((t) => t.projet_id === p.id);
    return {
      p,
      datees: siennes.filter((t) => t.date_debut || t.date_echeance),
      sansDate: siennes.filter((t) => !t.date_debut && !t.date_echeance).length,
    };
  }), [projets, taches]);

  // Au premier affichage : aujourd'hui au tiers gauche. Au zoom : on garde le
  // même jour au centre de l'écran. Si la frise s'allonge vers la gauche
  // (une tâche déplacée plus tôt), la vue ne saute pas.
  useLayoutEffect(() => {
    const z = zone.current;
    if (!z) return;
    const vis = z.clientWidth - gauche;
    if (ancre.current !== null) z.scrollLeft = Math.max(0, x(ancre.current) - vis / 2);
    else if (debutPrec.current === null) z.scrollLeft = Math.max(0, x(auj) - vis / 3);
    else z.scrollLeft += (debutPrec.current - debut) * zoom;
    ancre.current = null;
    debutPrec.current = debut;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, debut]);

  function changerZoom(z) {
    const el = zone.current;
    if (el) ancre.current = debut + (el.scrollLeft + (el.clientWidth - gauche) / 2) / zoom;
    setZoom(z);
    try { localStorage.setItem(CLE_ZOOM, String(z)); } catch { /* préférence locale facultative */ }
  }

  function allerAujourdhui() {
    const el = zone.current;
    if (el) el.scrollTo({ left: Math.max(0, x(auj) - (el.clientWidth - gauche) / 3), behavior: 'smooth' });
  }

  // ─── Glisser une barre ou un jalon ─────────────────────────────────────────
  // mode : 'deplacer' (le corps), 'debut' / 'fin' (un bord), 'jalon'.
  function commencer(e, t, mode) {
    if (e.button !== 0 || !onChangerDates) return;
    e.stopPropagation();
    const { a, b } = bornes(t);
    const g = {
      t, mode, a0: a, b0: b, a, b, bouge: false,
      x0: e.clientX + zone.current.scrollLeft, cx: e.clientX, cy: e.clientY, boucle: null,
    };
    geste.current = g;

    const calculer = () => {
      const d = Math.round((g.cx + zone.current.scrollLeft - g.x0) / zoom);
      if (mode === 'debut') { g.a = Math.min(g.a0 + d, g.b0); g.b = g.b0; }
      else if (mode === 'fin') { g.a = g.a0; g.b = Math.max(g.b0 + d, g.a0); }
      else { g.a = g.a0 + d; g.b = g.b0 + d; }
      setGlisse({ id: t.id, mode, a: g.a, b: g.b, cx: g.cx, cy: g.cy });
    };
    // Défilement automatique quand le curseur approche d'un bord de la frise.
    const defiler = () => {
      const el = zone.current;
      if (!el || geste.current !== g) return;
      const r = el.getBoundingClientRect();
      const pas = g.cx > r.right - BORD ? 14 : g.cx < r.left + gauche + BORD ? -14 : 0;
      if (pas) { el.scrollLeft += pas; calculer(); }
      g.boucle = requestAnimationFrame(defiler);
    };
    const bouger = (ev) => {
      g.cx = ev.clientX; g.cy = ev.clientY;
      if (!g.bouge) {
        if (Math.abs(ev.clientX + zone.current.scrollLeft - g.x0) < SEUIL) return;
        g.bouge = true;
        g.boucle = requestAnimationFrame(defiler);
      }
      ev.preventDefault();
      calculer();
    };
    const finir = (valider) => {
      window.removeEventListener('pointermove', bouger);
      window.removeEventListener('pointerup', lacher);
      window.removeEventListener('pointercancel', annuler);
      window.removeEventListener('keydown', echap);
      cancelAnimationFrame(g.boucle);
      geste.current = null;
      setGlisse(null);
      if (!g.bouge) return;
      sansClic.current = true;
      setTimeout(() => { sansClic.current = false; }, 0);
      if (!valider || (g.a === g.a0 && g.b === g.b0)) return;
      if (mode === 'jalon') {
        // Un jalon garde la date qu'il avait (début seul ou échéance seule).
        if (t.date_echeance) onChangerDates(t, null, versJour(g.a));
        else onChangerDates(t, versJour(g.a), null);
      } else {
        onChangerDates(t, versJour(g.a), versJour(g.b));
      }
    };
    const lacher = () => finir(true);
    const annuler = () => finir(false);
    const echap = (ev) => { if (ev.key === 'Escape') { ev.preventDefault(); finir(false); } };
    window.addEventListener('pointermove', bouger);
    window.addEventListener('pointerup', lacher);
    window.addEventListener('pointercancel', annuler);
    window.addEventListener('keydown', echap);
  }

  // Ne pas laisser un glissé en cours si la vue disparaît.
  useEffect(() => () => {
    if (geste.current) { cancelAnimationFrame(geste.current.boucle); geste.current = null; }
  }, []);

  const ouvrir = (t) => { if (!sansClic.current) onOuvrirTache(t); };

  // Libellé de mois : court quand la colonne est étroite, l'année en janvier
  // et sur le premier mois affiché.
  const libelleMois = (m, i) => {
    const w = m.jours * zoom;
    if (w < 26) return m.date.getUTCMonth() % 3 === 0 ? m.date.toLocaleDateString('fr-FR', { month: 'narrow', timeZone: 'UTC' }) : '';
    const avecAnnee = i === 0 || m.date.getUTCMonth() === 0;
    const txt = m.date.toLocaleDateString('fr-FR', { month: w < 60 ? 'short' : 'long', timeZone: 'UTC' });
    return `${txt}${avecAnnee && w >= 60 ? ` ${m.date.getUTCFullYear()}` : ''}`;
  };

  const grille = mois.map((m) => <div key={m.n} className="fresque-trait" style={{ left: x(m.n) }} />);
  const ligneAuj = auj >= debut && auj < fin ? <div className="fresque-aujourdhui" style={{ left: x(auj) + zoom / 2 }} /> : null;

  function infoTache(t) {
    return `${t.titre}\n${libelle(STATUTS_TACHE, t.statut)} · priorité ${libelle(PRIORITES, t.priorite).toLowerCase()}`
      + `${t.date_debut ? `\nDébut : ${dateCourte(t.date_debut)}` : ''}${t.date_echeance ? `\nÉchéance : ${dateCourte(t.date_echeance)}` : ''}`
      + `${t.assigne_id ? `\nAssignée à ${nomComplet(t, 'assigne_')}` : ''}`
      + `${onChangerDates ? '\n\nGlisser pour déplacer · tirer un bord pour changer une date' : ''}`;
  }

  function element(t) {
    const enCours = glisse && glisse.id === t.id ? glisse : null;
    const { a, b, jalon } = enCours ? { ...bornes(t), a: enCours.a, b: enCours.b } : bornes(t);
    const { classe, signe } = etat(t);
    const mobile = onChangerDates ? ' mobile' : '';
    const vise = enCours ? ' glisse' : '';
    if (jalon) {
      return (
        <button
          type="button"
          className={`fresque-jalon ${classe}${mobile}${vise}`}
          style={{ left: x(a) + zoom / 2 - 8 }}
          title={enCours ? undefined : infoTache(t)}
          onPointerDown={(e) => commencer(e, t, 'jalon')}
          onClick={() => ouvrir(t)}
        >
          <i aria-hidden="true" /><span>{t.titre}</span>
        </button>
      );
    }
    const w = Math.max((b - a + 1) * zoom, 10);
    const poignee = Math.min(8, w / 3);
    return (
      <button
        type="button"
        className={`fresque-barre ${classe}${mobile}${vise}`}
        style={{ left: x(a), width: w }}
        title={enCours ? undefined : infoTache(t)}
        onPointerDown={(e) => commencer(e, t, 'deplacer')}
        onClick={() => ouvrir(t)}
      >
        {onChangerDates && <i className="fresque-poignee debut" style={{ width: poignee }} onPointerDown={(e) => commencer(e, t, 'debut')} aria-hidden="true" />}
        {signe && <b aria-label="En retard">{signe}</b>}<span>{t.titre}</span>
        {onChangerDates && <i className="fresque-poignee fin" style={{ width: poignee }} onPointerDown={(e) => commencer(e, t, 'fin')} aria-hidden="true" />}
      </button>
    );
  }

  // Bulle qui suit le curseur pendant un glissé : la date visée.
  let bulle = null;
  if (glisse) {
    const duree = glisse.b - glisse.a + 1;
    const plage = <>{dateLongue(glisse.a)} → {dateLongue(glisse.b)} <em>· {duree} j</em></>;
    const contenu = {
      debut: <><small>Début</small><strong>{dateLongue(glisse.a)}</strong><em>{duree} j</em></>,
      fin: <><small>Échéance</small><strong>{dateLongue(glisse.b)}</strong><em>{duree} j</em></>,
      jalon: <><small>Date</small><strong>{dateLongue(glisse.a)}</strong></>,
      deplacer: <><small>Déplacer</small><strong>{plage}</strong></>,
    }[glisse.mode];
    bulle = <div className="fresque-bulle" role="status" style={{ left: glisse.cx, top: glisse.cy }}>{contenu}</div>;
  }

  return (
    <div className={`fresque-cadre${glisse ? ` en-glisse ${glisse.mode}` : ''}`}>
      <div className="fresque-outils">
        <div className="fresque-legende" aria-label="Légende">
          <span><i className="pastille-legende bleu" />À faire / en cours</span>
          <span><i className="pastille-legende orange" />Priorité haute</span>
          <span><i className="pastille-legende rouge" />En retard</span>
          <span><i className="pastille-legende vert" />Terminée</span>
          <span><i className="jalon-legende" />Jalon (une seule date)</span>
          <span><i className="bande-legende" />Dates du projet</span>
        </div>
        <span style={{ flex: 1 }} />
        <label className="fresque-zoom">
          Zoom
          <input type="range" min={ZOOM_MIN} max={ZOOM_MAX} step={1} value={zoom} onChange={(e) => changerZoom(Number(e.target.value))} />
        </label>
        <button type="button" className="btn btn-petit" onClick={allerAujourdhui}>Aller à aujourd’hui</button>
      </div>

      <div className="fresque" ref={zone}>
        <div className="fresque-ligne fresque-entete" style={{ width: gauche + largeur }}>
          <div className="fresque-gauche" style={{ width: gauche }}>Projet / tâche</div>
          <div className="fresque-droite" style={{ width: largeur }}>
            {mois.map((m, i) => (
              <div key={m.n} className="fresque-mois" style={{ left: x(m.n), width: m.jours * zoom }}>{libelleMois(m, i)}</div>
            ))}
            {glisse && <div className="fresque-repere" style={{ left: x(glisse.a), width: (glisse.b - glisse.a + 1) * zoom }} />}
            {ligneAuj}
          </div>
        </div>

        {groupes.map(({ p, datees, sansDate }) => (
          <React.Fragment key={p.id}>
            <div className="fresque-ligne fresque-ligne-projet" style={{ width: gauche + largeur }}>
              <div className="fresque-gauche" style={{ width: gauche }}>
                <button type="button" className="fresque-projet" onClick={() => onOuvrirProjet(p)} title={`Ouvrir le projet ${p.nom}`}>{p.nom}</button>
                <div className="fresque-qui">
                  <Avatar prenom={p.responsable_prenom} nom={p.responsable_nom} />
                  <span>{nomComplet(p, 'responsable_') || 'Sans responsable'}</span>
                </div>
                {(sansDate > 0 || datees.length === 0) && (
                  <div className="fresque-compte">
                    {datees.length === 0 && sansDate === 0 ? 'Aucune tâche'
                      : sansDate > 0 ? `${sansDate} tâche${sansDate > 1 ? 's' : ''} sans date` : ''}
                  </div>
                )}
              </div>
              <div className="fresque-droite" style={{ width: largeur }}>
                {grille}
                {(p.date_debut || p.date_echeance) && (
                  <div
                    className="fresque-bande"
                    title={`Projet : ${dateCourte(p.date_debut) || '…'} → ${dateCourte(p.date_echeance) || '…'}`}
                    style={{
                      left: x(jour(p.date_debut || p.date_echeance)),
                      width: Math.max(zoom, (jour(p.date_echeance || p.date_debut) - jour(p.date_debut || p.date_echeance) + 1) * zoom),
                    }}
                  />
                )}
                {ligneAuj}
              </div>
            </div>
            {datees.map((t) => (
              <div key={t.id} className="fresque-ligne fresque-ligne-tache" style={{ width: gauche + largeur }}>
                <div className="fresque-gauche" style={{ width: gauche }}>
                  <button type="button" className="fresque-tache" onClick={() => onOuvrirTache(t)} title={`Ouvrir la tâche ${t.titre}`}>
                    <span>{t.titre}</span>
                    {t.assigne_id && <Avatar prenom={t.assigne_prenom} nom={t.assigne_nom} titre={`Assignée à ${nomComplet(t, 'assigne_')}`} />}
                  </button>
                </div>
                <div className="fresque-droite" style={{ width: largeur }}>
                  {grille}
                  {ligneAuj}
                  {element(t)}
                </div>
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
      {bulle}
    </div>
  );
}
